import { Injectable } from '@angular/core';
import { Action, Actions, Selector, State, StateContext, Store } from '@ngxs/store';
import produce from 'immer';
import { Observable } from 'rxjs';
import { AbstractBrickSettingRemoteDto } from 'src/generated/signalR/abstract-brick-setting-remote-dto';
import { BrickSettingsService } from 'src/generated/swagger';
import { StageBrickSettingRemoteDto } from 'src/generated/swagger/model/stageBrickSettingRemoteDto';
import { IBrickSettingStateModel, SpecificBrickSettingsStateBase } from '../specific-brick-settings-base';
import { SetToolbarSettings } from '../toolbar-settings/toolbar-settings.actions';
import { ToolbarSettingsState } from '../toolbar-settings/toolbar-settings.state';
import { ChangeShowCallInfo, ChangeShowDefaultBackground, ChangeShowHistory, ChangeShowNextDialTarget } from './stage-settings.actions';

export interface StageSettingsStateModel extends IBrickSettingStateModel<StageBrickSettingRemoteDto> {}

function getDefaultSpecificBrickSetting(): StageBrickSettingRemoteDto {
   return {
      commonBrickSettingRemoteDto: null,
      showCallInfo: false,
      showHistory: false,
      showNextDialTarget: true,
      toolbarSetting: null,
   };
}

@State<StageSettingsStateModel>({
   name: 'stageSettings',
   defaults: {
      specificBrickSettings: {
         brickSettings: {},
         brickSettingsEditMode: null,
      },
   },
})
@Injectable()
export class StageSettingsState extends SpecificBrickSettingsStateBase<StageBrickSettingRemoteDto>(getDefaultSpecificBrickSetting) {
   constructor(private brickSettingsService: BrickSettingsService, store: Store, actions$: Actions) {
      super(store);
   }

   @Selector([StageSettingsState, StageSettingsState.getSettingModelFn])
   public static getShowCallInfo(state, getSettingModelFn: (state, brickInstanceId) => StageBrickSettingRemoteDto) {
      return (brickId: string) => {
         return getSettingModelFn(state, brickId).showCallInfo;
      };
   }

   @Selector([StageSettingsState, StageSettingsState.getSettingModelFn])
   public static getShowHistory(state, getSettingModelFn: (state, brickInstanceId) => StageBrickSettingRemoteDto) {
      return (brickId: string) => {
         return getSettingModelFn(state, brickId).showHistory;
      };
   }

   @Selector([StageSettingsState, StageSettingsState.getSettingModelFn])
   public static getShowNextDialTarget(state, getSettingModelFn: (state, brickInstanceId) => StageBrickSettingRemoteDto) {
      return (brickId: string) => {
         return getSettingModelFn(state, brickId).showNextDialTarget;
      };
   }

   @Selector([StageSettingsState, StageSettingsState.getSettingModelFn])
   public static getShowDefaultBackground(state, getSettingModelFn: (state, brickInstanceId) => StageBrickSettingRemoteDto) {
      return (brickId: string) => {
         return getSettingModelFn(state, brickId).showDefaultBackground;
      };
   }

   @Action(ChangeShowCallInfo)
   public changeShowCallInfo(ctx: StateContext<StageSettingsStateModel>, action: ChangeShowCallInfo) {
      const state = produce(ctx.getState(), (draft) => {
         const brickSettingModel = StageSettingsState.getSettingModelOrDefault(draft, action.brickInstanceId);
         brickSettingModel.showCallInfo = action.showCallInfo;
         draft.specificBrickSettings.brickSettingsEditMode[action.brickInstanceId] = brickSettingModel;
      });
      ctx.setState(state);
   }

   @Action(ChangeShowHistory)
   public changeShowHistory(ctx: StateContext<StageSettingsStateModel>, action: ChangeShowHistory) {
      const state = produce(ctx.getState(), (draft) => {
         const brickSettingModel = StageSettingsState.getSettingModelOrDefault(draft, action.brickInstanceId);
         brickSettingModel.showHistory = action.showHistory;
         draft.specificBrickSettings.brickSettingsEditMode[action.brickInstanceId] = brickSettingModel;
      });
      ctx.setState(state);
   }

   @Action(ChangeShowNextDialTarget)
   public changeShowNextDialTarget(ctx: StateContext<StageSettingsStateModel>, action: ChangeShowNextDialTarget) {
      const state = produce(ctx.getState(), (draft) => {
         const brickSettingModel = StageSettingsState.getSettingModelOrDefault(draft, action.brickInstanceId);
         brickSettingModel.showNextDialTarget = action.showNextDialTarget;
         draft.specificBrickSettings.brickSettingsEditMode[action.brickInstanceId] = brickSettingModel;
      });
      ctx.setState(state);
   }

   @Action(ChangeShowDefaultBackground)
   public changeShowDefaultBackground(ctx: StateContext<StageSettingsStateModel>, action: ChangeShowDefaultBackground) {
      const state = produce(ctx.getState(), (draft) => {
         const brickSettingModel = StageSettingsState.getSettingModelOrDefault(draft, action.brickInstanceId);
         brickSettingModel.showDefaultBackground = action.showDefaultBackground;
         draft.specificBrickSettings.brickSettingsEditMode[action.brickInstanceId] = brickSettingModel;
      });
      ctx.setState(state);
   }

   protected onPrepareBrickSettingsToSave(
      brickInstanceId: string,
      brickSetting: StageBrickSettingRemoteDto & AbstractBrickSettingRemoteDto
   ): StageBrickSettingRemoteDto & AbstractBrickSettingRemoteDto {
      brickSetting.toolbarSetting = this.store.selectSnapshot(ToolbarSettingsState.getToolbarEditModeSettings)(brickInstanceId, 'toolbar');
      return brickSetting;
   }

   protected doSaveSpecificSettings(brickInstanceId: string, brickSetting: StageBrickSettingRemoteDto): Observable<any> {
      return this.brickSettingsService.changeStageBrickSettings({
         brickInstanceId: brickInstanceId,
         settings: brickSetting,
      });
   }

   protected onSettingsUpdatedByServer(brickInstanceId: string, settings: StageBrickSettingRemoteDto): StageBrickSettingRemoteDto {
      this.store.dispatch(new SetToolbarSettings(brickInstanceId, 'toolbar', settings.toolbarSetting));
      return settings;
   }
}
